import React, { useEffect, useMemo, useState } from 'react';

import config from 'config/constants';
import FormRequestBlock from 'components/FormRequestBlock';
import { Badge } from 'components/ui/badge';
import { CopyableField } from 'components/ui/copyable-field';
import { Typography } from 'components/ui/typography';
import { useAppSelector } from 'app/store';
import { selectUserInfo } from 'app/store/user';
import { isLegacyBilling } from 'lib/utils';
import { INPUT_PATTERN, trackSegmentEvent } from 'app/utils';
import { ACTION, SOURCE } from 'app/analytics';
import { Lock } from 'lucide-react';
import { SAVE_WEBHOOK_URL } from 'app/graphql';
import { useMutation } from '@apollo/client/react';
import { canWrite } from 'app/access';

const WebhooksUrlBlock = () => {
  const userInfo = useAppSelector(selectUserInfo);
  const {
    webhookURL = '',
    webhookURLVerified = false,
    hmacSecretKey,
    billingPeriodSettings,
  } = userInfo || {};
  const canEditWebhookEndpoint = canWrite(userInfo, 'webhook_endpoints');

  // Legacy accounts still see the v1 webhook docs; modern accounts get the
  // Checkout-Intent webhook reference on docs.rye.com.
  const webhookDocsUrl = isLegacyBilling(billingPeriodSettings)
    ? config.external.doc.webhookUpdate
    : config.external.doc.webhooksV2;

  const [inputValue, setInputValue] = useState(webhookURL);

  const [saveWebhookUrl, saveWebhookUrlInfo] = useMutation(SAVE_WEBHOOK_URL, {
    context: { clientName: 'admin' },
  });

  const verified = useMemo(() => {
    // webhook was changed. Reset the verification status
    if (inputValue !== webhookURL && !saveWebhookUrlInfo.data) {
      return false;
    }

    if (saveWebhookUrlInfo.error) {
      return false;
    }

    return saveWebhookUrlInfo.data?.saveWebhookURL?.verified ?? webhookURLVerified;
  }, [saveWebhookUrlInfo, webhookURLVerified, inputValue, webhookURL]);

  // The user has saved before but the most recent attempt failed (or the
  // saved URL never verified). Distinguishing this from "never saved" lets
  // us show a louder error badge instead of a quiet neutral one.
  const verificationFailed =
    !!saveWebhookUrlInfo.error ||
    (saveWebhookUrlInfo.data && !saveWebhookUrlInfo.data.saveWebhookURL?.verified);

  useEffect(() => {
    setInputValue(webhookURL);
  }, [webhookURL]);

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!canEditWebhookEndpoint) {
      return;
    }
    trackSegmentEvent(SOURCE.WEBHOOK_MODULE, ACTION.CLICK, 'update_button');
    saveWebhookUrl({
      variables: {
        input: {
          url: inputValue,
        },
      },
    });
  };

  // The status pill swaps between three discrete states. Brief-by-design —
  // the long-form failure explanation lives in the body so the chrome stays
  // tight even when the URL is misconfigured.
  const statusDetail = useMemo(() => {
    if (verified) {
      return (
        <Badge variant="success">
          <span className="dot" />
          Verified
        </Badge>
      );
    }
    if (verificationFailed) {
      return (
        <Badge variant="error">
          <span className="dot" />
          Verification failed
        </Badge>
      );
    }
    if (webhookURL) {
      return (
        <Badge variant="neutral">
          <span className="dot" />
          Pending
        </Badge>
      );
    }
    return undefined;
  }, [verified, verificationFailed, webhookURL]);

  return (
    <FormRequestBlock
      title="Webhook endpoint"
      placeholder="Enter a URL for webhooks"
      statusDetail={statusDetail}
      onSubmit={handleSubmit}
      loading={saveWebhookUrlInfo.loading}
      required={true}
      type="url"
      disabled={!canEditWebhookEndpoint}
      pattern={INPUT_PATTERN.URL}
      inputValue={inputValue}
      onChange={setInputValue}
      text={
        <>
          <p>
            Webhook events are delivered as HTTP POST requests. When you save, we'll send a request
            with a <code>challenge</code> parameter — your endpoint must echo that value back for
            the URL to verify.{' '}
            <Typography variant="link" asChild>
              <a href={webhookDocsUrl} target="_blank" rel="noreferrer">
                View the docs
              </a>
            </Typography>{' '}
            for the full event catalog and signature format.
          </p>

          {verificationFailed && (
            <Typography variant="small" as="p" className="text-[var(--rye-red)]">
              Your endpoint didn't respond with the <code>challenge</code> value. Confirm the URL is
              reachable and echoes the parameter, then save again.
            </Typography>
          )}

          {hmacSecretKey && (
            <CopyableField
              label="HMAC secret:"
              value={hmacSecretKey}
              icon={<Lock className="h-4 w-4" />}
              copyButtonTitle="Copy HMAC secret"
            />
          )}
        </>
      }
    />
  );
};

export default WebhooksUrlBlock;
